import { getScanTemplateGroup, getScanTemplateOption, getScanTemplateTask } from '../ScanTemplateRoot';
import { ScanGroupOptimized, ScanRootOptimized } from './ScanRootOptimizedSchemas';

import { initGroupFromTemplate, initOptionFromTemplate, initTaskFromTemplate } from './initScanFromTemplate';
import { getScanGroup, getScanGroupCountByType, getScanOption, getScanTask, getScopeForScanPartChildren } from './ScanOptimizedGetters';
import { getScanWithUpdatedGroup, getScanWithUpdatedOption, getScanWithUpdatedTask, getUpdatedScan } from './ScanOptimizedPureFunctionalUpdates';

// **************************************************************
// Scan LINE ITEM DELETE
// **************************************************************

/** Removes one line item from its option; the option itself remains, even when emptied. */
export const deleteScanLineItem = (
  scan: ScanRootOptimized,
  groupId: string,
  taskId: string,
  optionId: string,
  lineItemId: string,
  now: number = Date.now(),
): ScanRootOptimized => {
  const option = getScanOption(scan, groupId, taskId, optionId);

  return getScanWithUpdatedOption(
    scan,
    groupId,
    taskId,
    optionId,
    { lineItems: option.lineItems?.filter(lineItem => lineItem.id !== lineItemId) },
    now,
  );
};

// **************************************************************
// Scan OPTION ADD/DELETE
// **************************************************************

export const addScanOption = (
  scan: ScanRootOptimized,
  groupId: string,
  taskId: string,
  optionTemplateId: string,
  now: number = Date.now(),
): ScanRootOptimized => {
  const group = getScanGroup(scan, groupId);
  const task = getScanTask(scan, groupId, taskId, group);
  const scopeForOption = getScopeForScanPartChildren(task);
  const optionTemplate = getScanTemplateOption(task.template, optionTemplateId);

  return getScanWithUpdatedTask(
    scan,
    groupId,
    taskId,
    { options: [...(task.options ?? []), initOptionFromTemplate(optionTemplate, scopeForOption, now)] },
    now,
  );
};

export const deleteScanOption = (
  scan: ScanRootOptimized,
  groupId: string,
  taskId: string,
  optionId: string,
  now: number = Date.now(),
): ScanRootOptimized => {
  const group = getScanGroup(scan, groupId);
  const task = getScanTask(scan, groupId, taskId, group);

  return getScanWithUpdatedTask(
    scan,
    groupId,
    taskId,
    { options: task.options?.filter(o => o.id !== optionId) },
    now,
  );
};

// **************************************************************
// Scan TASK ADD/DELETE
// **************************************************************

export const addScanTask = (
  scan: ScanRootOptimized,
  groupId: string,
  taskTemplateId: string,
  now?: number,
): ScanRootOptimized => {
  const group = getScanGroup(scan, groupId);
  const scopeForTask = getScopeForScanPartChildren(group);
  const taskTemplate = getScanTemplateTask(scan.template, group.template, taskTemplateId);

  return getScanWithUpdatedGroup(
    scan,
    groupId,
    { tasks: [...(group.tasks ?? []), initTaskFromTemplate(taskTemplate, scopeForTask, now)] },
    now,
  );
};

export const deleteScanTask = (
  scan: ScanRootOptimized,
  groupId: string,
  taskId: string,
  now?: number,
): ScanRootOptimized => {
  const group = getScanGroup(scan, groupId);
  return getScanWithUpdatedGroup(
    scan,
    groupId,
    { tasks: group.tasks?.filter(t => t.id !== taskId) },
    now,
  );
};

// **************************************************************
// Scan GROUP ADD/DELETE
// **************************************************************

export const addScanGroup = (
  scan: ScanRootOptimized,
  groupTemplateId: string,
  customLabel?: string,
  now: number = Date.now(),
): ScanRootOptimized => {
  const scanTemplate = scan.template;
  const groupTemplate = getScanTemplateGroup(scanTemplate, groupTemplateId);
  const group = initGroupFromTemplate(scan.template, groupTemplate, {}, now);
  if (!customLabel) {
    // count how many groups of this type are in the scan
    customLabel = `${groupTemplate.name} ${getScanGroupCountByType(scan, groupTemplateId) + 1}`;
  }
  group.customLabel = customLabel;

  return getUpdatedScan(
    scan,
    { groups: [...(scan.groups ?? []), group] },
    now,
  );
};

/**
 * can't remove the last group of a type
 * @param scan
 * @param groupId
 * @param cachedContext
 * @returns
 */
export const deleteScanGroup = (
  scan: ScanRootOptimized,
  groupId: string,
  now: number = Date.now(),
): ScanRootOptimized =>
  getUpdatedScan(
    scan,
    { groups: scan.groups?.filter(g => g.id !== groupId) },
    now,
  );

export const cloneGroup = (scanRoot: ScanRootOptimized, groupId: string): ScanRootOptimized => {
  const sourceGroup = getScanGroup(scanRoot, groupId);
  const newGroup: ScanGroupOptimized = initGroupFromTemplate(
    scanRoot.template,
    sourceGroup.template,
    scanRoot.optimized.optimizedValueExpressions.scope,
  );

  let lastIndex = 0;
  let nextNameNumber = 2;
  for (let i = 0; i < scanRoot.groups.length; i++) {
    const group = scanRoot.groups[i];
    if (group.templateId === sourceGroup.templateId) {
      lastIndex = i;
      if (group.customLabel && /\d$/.test(group.customLabel)) {
        const [numberString] = group.customLabel.match(/\d+/) ?? [];
        const nameNumber = parseInt(numberString!, 10);
        if (nameNumber >= nextNameNumber) nextNameNumber = nameNumber + 1;
      }
    }
  }

  // Set custom label
  newGroup.customLabel = nextNameNumber.toString();

  // Add to scan right after the original group
  return getUpdatedScan(scanRoot, {
    groups: scanRoot.groups.flatMap((group, i) =>
      i === lastIndex
        ? [group, newGroup]
        : [group]
    ),
  });
};
